// plugins/authentication.js
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { BroadcastChannel } from 'broadcast-channel';

const API_URL = process.env.VUE_APP_STATIC_API_URL || 'http://localhost:3001/api/v1';
const IGNORE_PATHS = ['/authentication/login', '/authentication/change-password'];
const PUBLIC_PATHS = ['/login', '/register', '/forgot-password'];
const TOKEN_EXPIRY_SKEW = 60 * 1000;
// const MAX_REFRESH_RETRIES = 3;

class AuthenticationManager {
  constructor($axios) {
    this.token = null;
    this.$axios = $axios; // ← store the injected instance
    this.refreshInProgress = false;
    this.tokenReadyPromise = null;
    this.channel = new BroadcastChannel('auth_channel');
    this.axiosAuth = axios.create({
      baseURL: API_URL,
      withCredentials: true,
    });
    this.init();
  }

  init() {
    this.loadTokenFromStorage();
    this.setupBroadcastChannel();
    this.setupAxiosInterceptors();
  }

  loadTokenFromStorage() {
    const storedToken = localStorage.getItem('accessToken');
    if (storedToken && this.isTokenValid(storedToken)) {
      this.updateToken(storedToken, 'load');
    } else {
      localStorage.removeItem('accessToken');
    }
  }

  setupBroadcastChannel() {
    this.channel.onmessage = ({ command, message }) => {
      if (command === 'askToken' && this.isTokenValid(this.token)) {
        this.channel.postMessage({ command: 'respondToken', message: this.token });
      } else if (command === 'respondToken' && this.isTokenValid(message)) {
        this.updateToken(message, 'tab-sync');
      } else if (command === 'setToken') {
        this.updateToken(message, 'manual-sync');
      } else if (command === 'logout') {
        this.clearToken('logout');
      }
    };
  }

  setupAxiosInterceptors() {
    axios.interceptors.request.use(async (config) => {
      if (config.offline || this.isIgnoredPath(config.url)) return config;
      await (this.tokenReadyPromise ||= this.ensureValidToken().finally(() => (this.tokenReadyPromise = null)));
      if (this.token) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${this.token}`;
      }
      return config;
    });

    axios.interceptors.response.use(
      (res) => res,
      async (err) => {
        const original = err.config;
        if (err.response?.status === 401 && !original._retry) {
          original._retry = true;
          try {
            await this.refreshToken();
            if (this.token) {
              original.headers['Authorization'] = `Bearer ${this.token}`;
              return axios(original);
            }
          } catch (e) {
            this.clearToken('refresh-failed');
            this.emitAuthError('refresh-failed', e);
          }
        }
        return Promise.reject(err);
      }
    );
  }

  async ensureValidToken() {
    if (this.isTokenValid(this.token)) return this.token;
    await this.requestTokenFromOtherTabs();
    if (!this.isTokenValid(this.token)) await this.refreshToken();
    return this.token;
  }

  async requestTokenFromOtherTabs() {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => resolve(null), 100);
      this.channel.onmessage = ({ command, message }) => {
        if (command === 'respondToken' && this.isTokenValid(message)) {
          clearTimeout(timeout);
          this.updateToken(message, 'received');
          resolve(message);
        }
      };
      this.channel.postMessage({ command: 'askToken' });
    });
  }

  async refreshToken() {
    if (this.refreshInProgress) {
      return new Promise((resolve, reject) => {
        const start = Date.now();
        const wait = () => {
          if (!this.refreshInProgress) return resolve(this.token);
          if (Date.now() - start > 5000) return reject(new Error('Refresh timeout'));
          setTimeout(wait, 100);
        };
        wait();
      });
    }
    this.refreshInProgress = true;
    try {
      const { data } = await this.axiosAuth.post('/authentication/refresh-token');
      this.updateToken(data.accessToken, 'refresh');
      this.broadcastToken(data.accessToken);
    } finally {
      this.refreshInProgress = false;
    }
  }

  updateToken(newToken, reason = null) {
    this.token = newToken;
    localStorage.setItem('accessToken', newToken);

    const authHeader = `Bearer ${newToken}`;
    axios.defaults.headers.common.Authorization = authHeader;
    this.axiosAuth.defaults.headers.common.Authorization = `Bearer ${newToken}`;

    if (this.$axios?.defaults?.headers?.common) {
      this.$axios.defaults.headers.common.Authorization = authHeader;
    }

    this.emitTokenEvent(newToken, reason);
  }

  clearToken(reason = null) {
    this.token = null;
    localStorage.removeItem('accessToken');
    this.emitTokenEvent(null, reason);
  }

  emitTokenEvent(token, reason) {
    window.dispatchEvent(new CustomEvent('tokenEvent', { detail: { token, reason } }));
  }

  emitAuthError(type, error) {
    window.dispatchEvent(new CustomEvent('authError', { detail: { type, error } }));
  }

  broadcastToken(token) {
    this.channel.postMessage({ command: 'setToken', message: token });
  }

  isTokenValid(token = this.token) {
    try {
      const decoded = jwtDecode(token);
      return decoded.exp * 1000 - Date.now() > TOKEN_EXPIRY_SKEW;
    } catch {
      return false;
    }
  }

  isIgnoredPath(url) {
    return IGNORE_PATHS.some((path) => url?.includes(path));
  }

  getToken() {
    return this.ensureValidToken().then(() => this.token);
  }

  isAuthenticated() {
    return this.ensureValidToken().then(() => this.isTokenValid());
  }

  async login(credentials) {
    const { data } = await this.axiosAuth.post('/authentication/login', credentials);
    this.updateToken(data.accessToken, 'login');
    this.broadcastToken(data.accessToken);
    return data;
  }

  async logout() {
    if (this.token) await this.axiosAuth.post('/authentication/logout');
    this.clearToken('logout');
    this.broadcastToken(null);
  }
}

var authManager;

const AuthPlugin = {
  install(Vue, { store, router }) {
    authManager = new AuthenticationManager(Vue.prototype.$axios);
    Vue.prototype.$auth = Vue.observable({ accessToken: authManager.token });

    Vue.prototype.$broadcastToken = (newToken) => {
      authManager.updateToken(newToken, 'manual');
      authManager.broadcastToken(newToken);
    };

    window.addEventListener('tokenEvent', ({ detail }) => {
      Vue.prototype.$auth.accessToken = detail.token;
      if (!detail.token) {
        store.dispatch('setUser', {});
        const path = detail.reason === 'logout' ? '/login' : '/login?error=tokenExpired';
        if (!PUBLIC_PATHS.includes(router.currentRoute.path)) {
          setTimeout(() => router.push(path).catch(() => {}), 100);
        }
      }
    });

    router.beforeEach(async (to, from, next) => {
      if (to.meta.requiresAuth && !PUBLIC_PATHS.includes(to.path)) {
        try {
          await authManager.ensureValidToken();
          if (!authManager.isTokenValid()) return next('/login');
        } catch {
          return next('/login');
        }
      }
      next();
    });
  },
};

export default AuthPlugin;
export { authManager };
